// PRODUCTION — large modal that renders the full live-updates preview (all
// pending changes). Two modes:
//   - 'confirm' (Save changes): Cancel + Confirm & save footer.
//   - 'view' (View fullscreen from the panel): single Close footer.

import React, { Fragment, memo } from 'react'
import { createPortal } from 'react-dom'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { getFlowEditorDraftValidation } from 'src/selectors/onboardingFlowEditor'
import { getTemplatePreviewData, getTemplatePreviewLoading } from 'src/selectors/templatePreview'
import {
  modalBackdropCss,
  modalBodyCss,
  modalCloseBtnCss,
  modalFooterActionsCss,
  modalFooterCss,
  modalHeaderCss,
  modalTitleCss,
  saveConfirmModalCardCss,
} from '../flowEditorModalStyles'
import { ConfigSection } from './ConfigSection'
import { ValidationBanner } from './ValidationBanner'

interface Props {
  open: boolean
  mode?: 'confirm' | 'view'
  saving?: boolean
  onClose: () => void
  onConfirm?: () => void
}

const FlowEditorChangesModalComponent = ({
  open,
  mode = 'confirm',
  saving = false,
  onClose,
  onConfirm,
}: Props) => {
  const data = useSelector(getTemplatePreviewData)
  const loading = useSelector(getTemplatePreviewLoading)
  const validation = useSelector(getFlowEditorDraftValidation)

  if (!open) {
    return null
  }

  const isConfirm = mode === 'confirm'
  const configs = data?.configs ?? []
  const stepCount = configs.reduce((total, config) => total + config.steps.length, 0)

  return createPortal(
    <Fragment>
      <div onClick={saving ? undefined : onClose} css={modalBackdropCss} />
      <div css={saveConfirmModalCardCss}>
        <div css={modalHeaderCss}>
          <div css={tw`flex flex-col gap-0.5`}>
            <span css={modalTitleCss}>
              {isConfirm ? 'Review changes before saving' : 'Live updates'}
            </span>
            <Typography variant="body2" css={tw`text-shades-500`}>
              {isConfirm
                ? 'These updates apply to the template and its contractors once you confirm.'
                : 'Everything that changes for contractors when you save.'}
            </Typography>
          </div>
          <button type="button" onClick={onClose} disabled={saving} css={modalCloseBtnCss}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div css={[modalBodyCss, tw`bg-shades-50`]}>
          {!validation.isValid && <ValidationBanner validation={validation} />}

          {configs.length > 0 && (
            <div css={tw`flex flex-col gap-4`}>
              {configs.map((config) => {
                return (
                  <div
                    key={config.configLabel}
                    css={tw`rounded-xl border border-shades-200 bg-shades-0 p-4 border-solid`}
                  >
                    <ConfigSection config={config} />
                  </div>
                )
              })}
            </div>
          )}

          {/* An invalid draft (e.g. an empty group) yields zero configs; the
              validation banner above explains it, so skip the misleading
              "No pending changes" placeholder in that case. */}
          {configs.length === 0 && (loading || validation.isValid) && (
            <div css={tw`flex flex-col items-center gap-2 px-4 py-12 text-center`}>
              <GSIcon iconName="gs_eye_open" width={28} height={28} css={tw`fill-shades-300`} />
              <Typography variant="h6" css={tw`text-shades-700`}>
                {loading ? 'Loading preview…' : 'No pending changes'}
              </Typography>
              <Typography variant="body2" css={tw`text-shades-400`}>
                {loading
                  ? 'Fetching the impact of your changes…'
                  : 'Edit a step or group to see who it will affect.'}
              </Typography>
            </div>
          )}
        </div>

        <div css={modalFooterCss}>
          <span css={tw`text-[13px] text-shades-500`}>
            {stepCount > 0
              ? `${stepCount} step${stepCount > 1 ? 's' : ''} affected across ${
                  configs.length
                } group${configs.length > 1 ? 's' : ''}`
              : ''}
          </span>
          <div css={modalFooterActionsCss}>
            {isConfirm ? (
              <Fragment>
                <Button
                  variant="outlined"
                  size="small"
                  color="white"
                  onClick={onClose}
                  disabled={saving}
                  className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="primary"
                  loading={saving}
                  onClick={onConfirm}
                  className={tw`!bg-success-500 hover:!bg-success-500 active:!bg-success-500`}
                >
                  Confirm &amp; save
                </Button>
              </Fragment>
            ) : (
              <Button
                variant="outlined"
                size="small"
                color="white"
                onClick={onClose}
                className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </Fragment>,
    document.body,
  )
}

export const FlowEditorChangesModal = memo(FlowEditorChangesModalComponent)
